import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Sparkles, Volume2, Languages, ArrowRight, Feather, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ttsOK } from "@/lib/languages";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Paywall from "@/components/Paywall";
import BundleHint from "@/components/BundleHint";
import { useI18n } from "@/context/I18nContext";

const THEMES = [
  { key: "mystery", label: "Mystery", emoji: "🔍", theme: "a mysterious disappearance in a small town" },
  { key: "adventure", label: "Adventure", emoji: "🗺️", theme: "an unexpected journey across the country" },
  { key: "romance", label: "Romance", emoji: "💌", theme: "two strangers who keep meeting by chance" },
  { key: "scifi", label: "Sci-Fi", emoji: "🚀", theme: "a traveler who wakes up in the future" },
  { key: "slice", label: "Slice of life", emoji: "☕", theme: "a surprising ordinary day in the city" },
];

export default function Story() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [theme, setTheme] = useState("");
  const [showTrans, setShowTrans] = useState({});
  const [paywall, setPaywall] = useState(false);
  const [narrating, setNarrating] = useState(null);

  useEffect(() => {
    api.get("/story").then((r) => setStory(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const speak = async (text, key) => {
    if (narrating) return;
    setNarrating(key);
    try {
      const res = await api.post("/tts", { text, voice: user?.tts_voice || "nova", speed: user?.tts_speed || 1.0 });
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      audio.onended = () => setNarrating(null);
      audio.onerror = () => setNarrating(null);
      await audio.play();
    } catch (e) {
      setNarrating(null);
      toast.error(t("pm.common.voiceUnavailable"));
    }
  };

  const start = async (t) => {
    setBusy(true);
    try {
      const res = await api.post("/story/start", { theme: t || theme || null });
      setStory(res.data);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(true);
      else toast.error(e?.response?.data?.detail || t("pm.story.startFailed"));
    } finally { setBusy(false); }
  };

  const next = async () => {
    setBusy(true);
    try {
      const res = await api.post("/story/next", { story_id: story.id });
      setStory((s) => ({ ...s, chapters: [...s.chapters, res.data.chapter], can_continue_today: false }));
      toast.success(t("pm.story.unlocked", { n: res.data.n }));
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(true);
      else toast.error(t("pm.story.continueFailed"));
    } finally { setBusy(false); }
  };

  if (loading) return <AppLayout><div className="py-24 flex justify-center"><div className="w-12 h-12 border-4 border-ink border-t-coral rounded-full animate-spin" /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2"><BookOpen className="w-6 h-6 text-coral" /><h1 className="font-cabinet font-extrabold text-3xl">{t("pm.story.title")}</h1></div>
        <p className="text-gray-600">{t("pm.story.subtitle")}</p>
        <BundleHint id="story">{t("pm.story.hint")}</BundleHint>

        {!story ? (
          <div className="nb-card bg-lavender p-6 mt-6" data-testid="story-start">
            <Feather className="w-10 h-10" />
            <h3 className="font-cabinet font-bold text-xl mt-3">{t("pm.story.beginTitle")}</h3>
            <p className="text-sm text-gray-700">{t("pm.story.beginSub")}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {THEMES.map((th) => (
                <button key={th.label} onClick={() => start(th.theme)} disabled={busy}
                  className="nb-tag bg-white hover:shadow-hard-sm" data-testid={`story-theme-${th.label.toLowerCase().replace(/\s/g,'-')}`}>
                  {th.emoji} {t(`pm.story.themes.${th.key}`)}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <input className="nb-input flex-1" placeholder={t("pm.story.ownIdea")} value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="story-theme-input" />
              <button onClick={() => start()} disabled={busy} className="nb-btn-primary px-5" data-testid="story-start-btn">
                {busy ? t("pm.story.writing") : <>{t("pm.story.start")} <ArrowRight className="w-5 h-5 rtl:rotate-180" /></>}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6" data-testid="story-reader">
            <div className="nb-card bg-butter p-5">
              <div className="nb-tag bg-white">{t("pm.story.chapters", { count: story.chapters.length })}</div>
              <h2 className="font-cabinet font-extrabold text-3xl mt-2" dir="auto">{story.title}</h2>
              {story.title_translation && <p className="text-sm text-gray-600" dir="auto">{story.title_translation}</p>}
            </div>

            <div className="space-y-4 mt-5">
              {story.chapters.map((c) => (
                <motion.div key={c.n} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="nb-card p-6" data-testid="story-chapter">
                  <div className="flex items-center justify-between">
                    <div className="font-cabinet font-bold text-lg">{t("pm.story.chapter", { n: c.n })}</div>
                    <div className="flex gap-2">
                      {ttsOK(user?.target_lang) && <button onClick={() => speak(c.text, c.n)} disabled={narrating === c.n} className="w-8 h-8 rounded-lg border-2 border-ink bg-white flex items-center justify-center" title={t("pm.story.listen")} aria-label={t("pm.story.listen")} data-testid={`story-listen-${c.n}`}>
                        {narrating === c.n ? <span className="w-3 h-3 border-2 border-ink border-t-transparent rounded-full animate-spin" /> : <Volume2 className="w-4 h-4" />}
                      </button>}
                      <button onClick={() => setShowTrans((s) => ({ ...s, [c.n]: !s[c.n] }))} className={`w-8 h-8 rounded-lg border-2 border-ink flex items-center justify-center ${showTrans[c.n] ? "bg-mint" : "bg-white"}`} title={t("pm.story.translation")} aria-label={t("pm.story.translation")} data-testid={`story-translation-${c.n}`}><Languages className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <p className="font-cabinet text-lg leading-relaxed mt-3" dir="auto">{c.text}</p>
                  {showTrans[c.n] && <p className="text-sm text-gray-500 mt-2 border-s-4 border-mint ps-3" dir="auto">{c.translation}</p>}
                  {c.vocab_used?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {c.vocab_used.map((v, i) => (
                        <span key={`${v.word}-${i}`} className="nb-tag bg-mint/50 text-[11px]">{v.word} · {v.translation}</span>
                      ))}
                    </div>
                  )}
                  {c.cliffhanger && <p className="text-sm italic text-coral font-bold mt-4" dir="auto">— {c.cliffhanger}</p>}
                </motion.div>
              ))}
            </div>

            <button onClick={next} disabled={busy} className="nb-btn-primary w-full mt-6 py-4 text-lg" data-testid="story-next-btn">
              {busy ? t("pm.story.writingNext") : <><Sparkles className="w-5 h-5" /> {t("pm.story.continue")}</>}
            </button>
            <button onClick={() => { setStory(null); setTheme(""); }} className="w-full text-sm text-gray-500 mt-3" data-testid="story-different-btn">{t("pm.story.different")}</button>
          </div>
        )}
      </div>
      <Paywall open={paywall} onClose={() => setPaywall(false)} reason={t("pm.common.proFeature", { name: t("pm.story.title") })} />
    </AppLayout>
  );
}
